import { Box, Card, Icon, ResourceItem, ResourceList, Text, TextField, useBreakpoints } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchIcon } from '@shopify/polaris-icons';
import helpers from 'helpers/index';
import { TypedVehicle, useMutateGetVehicles } from 'queries/vehicle.query';

// Định nghĩa props cho component
type QuickSearchVehicleProps = {
  /** Vehicle được chọn ban đầu (nếu có) */
  initialVehicle?: TypedVehicle | null;
  /** Callback được gọi khi người dùng chọn một xe, trả về object xe đã chọn hoặc null */
  onSelect: (vehicle: TypedVehicle | null) => void;
  /** Tiêu đề của ô tìm kiếm */
  title: string;
  /** Placeholder cho ô tìm kiếm */
  placeholder?: string;
  /** Trạng thái vô hiệu hóa component */
  disabled?: boolean;
  /** Trạng thái chỉ đọc, không cho phép thay đổi */
  readonly?: boolean;
};

/**
 *
 * @param current_user_id bigint
 * @param  onSelect callback with bigint userid
 * @returns
 */
export default function QuickSearchVehicle({
  disabled,
  onSelect,
  title = 'Tìm xe theo biển số',
  placeholder = 'Tìm xe theo biển số',
  readonly = false,
}: QuickSearchVehicleProps) {
  const { mutateAsync: searchVehicle, isSuccess } = useMutateGetVehicles();

  const initialQuery = {
    query: '',
    sort: 'createdAt:desc',
    vehicle_status: 1,
    limit: 20,
  };

  const [mainQuery, setMainQuery] = useState(initialQuery);

  /**
   * User list
   */
  const [vehicleList, setVehicleList] = useState<any[]>([]);

  const onQueryToServer = useCallback(async (queryString) => {
    try {
      let data = await searchVehicle(queryString);
      // @ts-ignore
      setVehicleList(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (mainQuery) {
      onQueryToServer(mainQuery);
    }
  }, [mainQuery]);

  /**
   * Assign cho ai đóa
   */
  const [userQuickSearchText, setUserQuickSearchText] = useState<string>('');
  const [userListForQuickOption, setUserListForQuickOption] = useState([]);

  const onQueryToServerDebound = useCallback((keyword) => {
    setMainQuery((old) => ({ ...old, ...{ query: keyword } }));
  }, []);

  useEffect(() => {
    let innerUser = [];
    if (vehicleList)
      for (let vehicle of vehicleList) {
        innerUser.push({
          id: vehicle.id,
          name: vehicle.name,
          note: vehicle.note,
          license_plate: vehicle.license_plate,
        });
      }
    setUserListForQuickOption(innerUser);
  }, [vehicleList]);

  const onChangeMainQueryCallback = useMemo(() => {
    return helpers.debounce((_value: string) => {
      onQueryToServerDebound?.call(this, _value);
    }, 400);
  }, []);

  const updateUserText = useCallback((value: string) => {
    setUserQuickSearchText(value);
    onChangeMainQueryCallback(value);
  }, []);

  const findUserSelectedInEntities = useCallback(
    (id) => {
      if (Array.isArray(vehicleList)) {
        let a = vehicleList.filter((a) => a.id === id);
        return a[0] || null;
      }
    },
    [vehicleList]
  );

  const updateSelection = useCallback(
    (id: string) => {
      const matchedOption = userListForQuickOption.find((option) => {
        return option.id === id;
      });
      setUserQuickSearchText(matchedOption.license_plate || '');
      // @ts-ignore
      onSelect?.call(this, findUserSelectedInEntities(matchedOption.id));
    },
    [userListForQuickOption]
  );
  const { smUp } = useBreakpoints();

  return (
    <Box minWidth="250px" maxWidth={smUp ? `300px` : '100%'} padding={{ xs: '0', sm: '0', md: '200' }}>
      <Card>
        <TextField
          readOnly={readonly}
          onChange={updateUserText}
          label={title}
          disabled={disabled}
          value={userQuickSearchText}
          prefix={<Icon source={SearchIcon} tone="base" />}
          placeholder={placeholder}
          autoComplete="off"
          clearButton
          onClearButtonClick={() => updateUserText('')}
        />
      </Card>
      <ResourceList
        emptyState={
          isSuccess && helpers.isEmpty(userListForQuickOption) ? (
            <Box padding={'400'}>
              <Text as="p" tone="subdued">
                Chưa thấy chiếc xe nào được cài đặt!
              </Text>
            </Box>
          ) : null
        }
        resourceName={{ singular: 'vehicle', plural: 'vehicle' }}
        items={userListForQuickOption}
        renderItem={(item) => {
          const { license_plate, id, name, note } = item;

          return (
            <ResourceItem verticalAlignment="center" id={id} onClick={() => updateSelection(id)} media={null}>
              <Text variant="bodyMd" fontWeight="bold" as="p">
                {license_plate} (<i>{name}</i>)
              </Text>
              {note ? (
                <Text variant="bodyMd" tone="subdued" as="p">
                  {note}
                </Text>
              ) : null}
            </ResourceItem>
          );
        }}
      />
    </Box>
  );
}

